import type { IChatKitWidgetInstance } from '../interfaces/chatkit-widget.interface';
import type {
  IChatKitWidgetCache,
  IChatKitWidgetThreadCache,
  IChatKitWidgetTurnCache,
} from '../interfaces/local-storage';
import { LocalStoreHandler } from './local-storage-handler';

const MAX_THREADS = 10;
const MAX_TURNS_PER_THREAD = 80;
const MAX_WIDGETS_PER_TURN = 12;

export const ChatKitWidgetCache = {
  saveWidgets(
    threadId: string | null | undefined,
    questionId: string | null | undefined,
    widgets: IChatKitWidgetInstance[] | undefined
  ): void {
    if (!threadId || !questionId || !widgets?.length) return;

    try {
      const store = LocalStoreHandler.get() ?? {};
      const cache = normalizeCache(store.chatKitWidgetCache);
      const now = new Date().toISOString();
      const thread = normalizeThread(cache.threads[threadId]);
      const turn = normalizeTurn(thread.turns[questionId]);
      const merged = mergeWidgets(turn.widgets, widgets).slice(
        -MAX_WIDGETS_PER_TURN
      );

      thread.turns[questionId] = {
        updatedAt: now,
        widgets: merged,
      };
      thread.updatedAt = now;
      cache.threads[threadId] = pruneThread(thread);

      LocalStoreHandler.update({
        chatKitWidgetCache: pruneCache(cache),
      });
    } catch {
      // Widget persistence is best-effort only; rendering should never fail on storage errors.
    }
  },

  getWidgets(
    threadId: string | null | undefined,
    questionId: string | null | undefined
  ): IChatKitWidgetInstance[] {
    if (!threadId || !questionId) return [];

    try {
      const cache = LocalStoreHandler.get()?.chatKitWidgetCache;
      const widgets = cache?.threads?.[threadId]?.turns?.[questionId]?.widgets;
      return cloneWidgets(widgets ?? []);
    } catch {
      return [];
    }
  },

  clearThread(threadId: string | null | undefined): void {
    if (!threadId) return;

    try {
      const store = LocalStoreHandler.get() ?? {};
      const cache = normalizeCache(store.chatKitWidgetCache);
      if (!cache.threads[threadId]) return;
      delete cache.threads[threadId];
      LocalStoreHandler.update({ chatKitWidgetCache: cache });
    } catch {
      // Best-effort cleanup only.
    }
  },
};

function normalizeCache(cache: IChatKitWidgetCache | undefined): IChatKitWidgetCache {
  return {
    threads: cache?.threads && typeof cache.threads === 'object'
      ? { ...cache.threads }
      : {},
  };
}

function normalizeThread(
  thread: IChatKitWidgetThreadCache | undefined
): IChatKitWidgetThreadCache {
  return {
    updatedAt: thread?.updatedAt ?? new Date(0).toISOString(),
    turns: thread?.turns && typeof thread.turns === 'object'
      ? { ...thread.turns }
      : {},
  };
}

function normalizeTurn(
  turn: IChatKitWidgetTurnCache | undefined
): IChatKitWidgetTurnCache {
  return {
    updatedAt: turn?.updatedAt ?? new Date(0).toISOString(),
    widgets: Array.isArray(turn?.widgets) ? turn.widgets : [],
  };
}

function mergeWidgets(
  existing: IChatKitWidgetInstance[],
  next: IChatKitWidgetInstance[]
): IChatKitWidgetInstance[] {
  const byKey = new Map<string, IChatKitWidgetInstance>();
  [...existing, ...next].forEach((widget) => {
    const key = widget.toolCallId || widget.id;
    if (key) byKey.set(key, cloneWidget(widget));
  });
  return [...byKey.values()];
}

function pruneThread(
  thread: IChatKitWidgetThreadCache
): IChatKitWidgetThreadCache {
  const turns = Object.entries(thread.turns)
    .sort(([, left], [, right]) => left.updatedAt.localeCompare(right.updatedAt))
    .slice(-MAX_TURNS_PER_THREAD);
  return {
    ...thread,
    turns: Object.fromEntries(turns),
  };
}

function pruneCache(cache: IChatKitWidgetCache): IChatKitWidgetCache {
  const threads = Object.entries(cache.threads)
    .sort(([, left], [, right]) => left.updatedAt.localeCompare(right.updatedAt))
    .slice(-MAX_THREADS);
  return {
    threads: Object.fromEntries(threads),
  };
}

function cloneWidgets(
  widgets: IChatKitWidgetInstance[]
): IChatKitWidgetInstance[] {
  return widgets.map(cloneWidget);
}

function cloneWidget(widget: IChatKitWidgetInstance): IChatKitWidgetInstance {
  return JSON.parse(JSON.stringify(widget));
}
